/**
 * emv.ts — Codec QR au standard EMVCo Merchant-Presented Mode (MPM),
 * le même format que le BR Code brésilien (Pix), l'UPI indien, SGQR, etc.
 *
 * Un QR EMV est une suite de blocs TLV (Tag-Length-Value) :
 *   ID (2 chiffres) + Longueur (2 chiffres) + Valeur
 *
 * Avantages pour Monegasy : format international, prêt pour un futur rail
 * interopérable, et porteur de données structurées (montant, devise,
 * référence) lisibles par n'importe quel lecteur conforme.
 *
 * Ce module est volontairement sans dépendance (utilisable côté SDK, web,
 * mobile et serveur).
 */

/** Identifiant Monegasy dans le template "Merchant Account Information". */
export const MONEGASY_GUID = "com.monegasy";

/** Devise Ariary malgache — ISO 4217 numérique (MGA = 969). */
export const CURRENCY_MGA = "969";

// ── IDs EMV racine ──────────────────────────────────────────────────────
const ID_PAYLOAD_FORMAT = "00";
const ID_POI_METHOD = "01";
const ID_MERCHANT_ACCT_MONEGASY = "26"; // template 26-51
const ID_MCC = "52";
const ID_CURRENCY = "53";
const ID_AMOUNT = "54";
const ID_COUNTRY = "58";
const ID_MERCHANT_NAME = "59";
const ID_MERCHANT_CITY = "60";
const ID_ADDITIONAL = "62"; // template additionnel
const ID_CRC = "63";

// Sous-IDs du template marchand (26)
const SUB_GUID = "00";
const SUB_MERCHANT_ID = "01";

// Sous-IDs du template additionnel (62)
const SUB_BILL_NUMBER = "01";
const SUB_REFERENCE_LABEL = "05";

// ── Helpers TLV ─────────────────────────────────────────────────────────

/** Construit un bloc TLV : id + longueur(2) + valeur. */
function tlv(id: string, value: string): string {
  const len = value.length.toString().padStart(2, "0");
  return id + len + value;
}

/**
 * CRC-16/CCITT-FALSE (poly 0x1021, init 0xFFFF) — checksum EMV.
 * Calculé sur toute la chaîne, incluant "6304".
 */
export function crc16ccitt(data: string): string {
  let crc = 0xffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
      crc &= 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
}

// ── Encodage ────────────────────────────────────────────────────────────

export interface EmvQrParams {
  /** Identifiant marchand (merchantId / userId / lien de paiement). */
  merchantId: string;
  /** Nom commercial (≤ 25 car., tronqué). */
  merchantName: string;
  /** Ville (≤ 15 car.). Défaut "Antananarivo". */
  merchantCity?: string;
  /** Montant. Si fourni → QR dynamique (one-time), sinon statique. */
  amount?: number;
  /** Référence / numéro de facture (apparait dans le template 62). */
  reference?: string;
  /** Devise ISO 4217 numérique. Défaut MGA (969). */
  currency?: string;
  /** Code pays ISO 3166-1 alpha-2. Défaut "MG". */
  countryCode?: string;
  /** Merchant Category Code. Défaut "0000". */
  mcc?: string;
}

/**
 * Construit un QR EMV (BR Code) Monegasy. Le résultat est la chaîne à
 * encoder dans le QR (ex. "00020101021126..6304XXXX").
 */
export function buildMonegasyEmvQr(params: EmvQrParams): string {
  const {
    merchantId,
    merchantName,
    merchantCity = "Antananarivo",
    amount,
    reference,
    currency = CURRENCY_MGA,
    countryCode = "MG",
    mcc = "0000",
  } = params;

  const isDynamic = typeof amount === "number" && amount > 0;

  // Template marchand (26) : GUID Monegasy + merchantId
  const merchantTemplate =
    tlv(SUB_GUID, MONEGASY_GUID) + tlv(SUB_MERCHANT_ID, merchantId);

  let payload =
    tlv(ID_PAYLOAD_FORMAT, "01") +
    tlv(ID_POI_METHOD, isDynamic ? "12" : "11") +
    tlv(ID_MERCHANT_ACCT_MONEGASY, merchantTemplate) +
    tlv(ID_MCC, mcc) +
    tlv(ID_CURRENCY, currency);

  if (isDynamic) {
    payload += tlv(ID_AMOUNT, amount!.toFixed(2));
  }

  payload +=
    tlv(ID_COUNTRY, countryCode) +
    tlv(ID_MERCHANT_NAME, merchantName.slice(0, 25)) +
    tlv(ID_MERCHANT_CITY, merchantCity.slice(0, 15));

  if (reference) {
    const additional = tlv(SUB_REFERENCE_LABEL, reference.slice(0, 25));
    payload += tlv(ID_ADDITIONAL, additional);
  }

  // CRC : on ajoute "6304" puis le checksum des 4 hex.
  const toCrc = payload + ID_CRC + "04";
  return toCrc + crc16ccitt(toCrc);
}

// ── Décodage ────────────────────────────────────────────────────────────

export interface ParsedEmvQr {
  formatIndicator?: string;
  poiMethod?: string;
  /** Templates marchands trouvés (26-51) avec leur GUID + données. */
  merchantAccounts: { id: string; guid?: string; merchantId?: string }[];
  mcc?: string;
  currency?: string;
  amount?: number;
  countryCode?: string;
  merchantName?: string;
  merchantCity?: string;
  reference?: string;
  crc?: string;
  crcValid: boolean;
  /** Présent si c'est un QR Monegasy (GUID com.monegasy reconnu). */
  monegasy?: { merchantId: string };
}

/** Parse une chaîne TLV en map id → valeur (premier niveau). */
function parseTlv(s: string): { id: string; value: string }[] {
  const out: { id: string; value: string }[] = [];
  let i = 0;
  while (i + 4 <= s.length) {
    const id = s.slice(i, i + 2);
    const len = parseInt(s.slice(i + 2, i + 4), 10);
    if (isNaN(len)) break;
    const value = s.slice(i + 4, i + 4 + len);
    if (value.length < len) break; // tronqué
    out.push({ id, value });
    i += 4 + len;
  }
  return out;
}

/** Heuristique rapide : ressemble à un QR EMV ? */
export function isEmvQr(s: string): boolean {
  return /^000201/.test(s.trim()) && s.includes("63" + "04");
}

/**
 * Parse un QR EMV. Renvoie null si le format n'est pas reconnu.
 * Valide le CRC et extrait les champs Monegasy si présents.
 */
export function parseEmvQr(raw: string): ParsedEmvQr | null {
  const s = raw.trim();
  if (!isEmvQr(s)) return null;

  const crcIndex = s.lastIndexOf(ID_CRC + "04");
  const crcValid =
    crcIndex > 0
      ? crc16ccitt(s.slice(0, crcIndex + 4)) === s.slice(crcIndex + 4)
      : false;

  const top = parseTlv(s);
  const result: ParsedEmvQr = { merchantAccounts: [], crcValid };

  for (const { id, value } of top) {
    switch (id) {
      case ID_PAYLOAD_FORMAT:
        result.formatIndicator = value;
        break;
      case ID_POI_METHOD:
        result.poiMethod = value;
        break;
      case ID_MCC:
        result.mcc = value;
        break;
      case ID_CURRENCY:
        result.currency = value;
        break;
      case ID_AMOUNT:
        result.amount = parseFloat(value);
        break;
      case ID_COUNTRY:
        result.countryCode = value;
        break;
      case ID_MERCHANT_NAME:
        result.merchantName = value;
        break;
      case ID_MERCHANT_CITY:
        result.merchantCity = value;
        break;
      case ID_CRC:
        result.crc = value;
        break;
      case ID_ADDITIONAL: {
        const sub = parseTlv(value);
        const ref =
          sub.find((x) => x.id === SUB_REFERENCE_LABEL)?.value ??
          sub.find((x) => x.id === SUB_BILL_NUMBER)?.value;
        if (ref) result.reference = ref;
        break;
      }
      default: {
        // Templates marchands 26-51
        const idNum = parseInt(id, 10);
        if (idNum >= 26 && idNum <= 51) {
          const sub = parseTlv(value);
          const guid = sub.find((x) => x.id === SUB_GUID)?.value;
          const merchantId = sub.find((x) => x.id === SUB_MERCHANT_ID)?.value;
          result.merchantAccounts.push({ id, guid, merchantId });
          if (guid === MONEGASY_GUID && merchantId) {
            result.monegasy = { merchantId };
          }
        }
      }
    }
  }

  return result;
}
